import { useContext, useEffect, useState } from "react";
import { useHistory } from "react-router";
import { PaymentInterval } from "../../../../../../common/premium_tiers";
import { DashboardUtils } from "../../../dashboard_utils";
import { trpc } from "../../../utils/trpc";
import { setDocTitle } from "../../../web_ui";
import { BackButton } from "../base_components/back_button";
import { DashboardContainer } from "../base_components/dashboard_container";
import { PremiumActivePurchaseListings } from "./premium_active_purchase_listings";
import { PricingTable } from "./pricing_table";
import { RestoreDiscordPurchasesButton } from "./restore_discord_purchases_button";
import { GifFeatureSections } from "../../../../ssr/premium/components/gif-feature-sections";
import { FeatureComparisonTable } from "../../../../ssr/premium/components/feature-comparison-table";
import { CustomBotCard } from "../../../../ssr/premium/components/custom-bot-card";
import { SimpleContactModal } from "../../../../ssr/premium/components/simple-contact-modal";
import { PremiumFaq } from "../../../../ssr/premium/components/premium-faq";
import { SaleBanner } from "./sale_banner";
import { ProQuantityModal } from "./pro_quantity_modal";
import { SubscriptionUpgradeModal } from "./subscription_upgrade_modal";
import { ServerActivationModal, ActivationType } from "./pro_assignment_modal";
import { GuildListContext } from "../../../contexts";

export function PremiumPage(props: { back_link?: string }) {
	useEffect(() => {
		setDocTitle("Premium");
	}, []);

	const [showContactModal, setShowContactModal] = useState(false);
	const history = useHistory();

	// Upgrade flow state
	const [selectedLicense, setSelectedLicense] = useState<{
		id: string;
		product_id: string;
		is_bundle: boolean;
		available_pro_slots: number;
		current_pro_addon_count: number;
		payment_interval?: number;
	} | null>(null);
	const [showQuantityModal, setShowQuantityModal] = useState(false);
	const [showSubscriptionConfirm, setShowSubscriptionConfirm] = useState(false);
	const [selectedProCount, setSelectedProCount] = useState(1);
	const [showActivationModal, setShowActivationModal] = useState(false);
	const [pendingUnassignedCount, setPendingUnassignedCount] = useState<number | null>(null);
	const [activationType, setActivationType] = useState<ActivationType>("pro");
	const [isRedirectingToCheckout, setIsRedirectingToCheckout] = useState(false);

	const guild_list = useContext(GuildListContext);
	const prices_query = trpc.premium.getAllProductsPrices.useQuery();
	const premium_details_query = trpc.premium.getUserPremiumDetails.useQuery();
	const upgradeableLicensesQuery = trpc.premium.getUpgradeableLicenses.useQuery();
	const trpcContext = trpc.useContext();

	// Mutation for assigning Pro to guilds - must be before early return to satisfy React hooks rules
	const assignProMutation = trpc.premium.assignSeshProToGuilds.useMutation({
		onSuccess: () => {
			setShowActivationModal(false);
			setPendingUnassignedCount(null);
			trpcContext.premium.invalidate();
		},
	});

	if (!prices_query.isSuccess || !premium_details_query.isSuccess || isRedirectingToCheckout) {
		return <DashboardUtils.RenderFullLoadingThrobberPage />;
	}

	// Helper to proceed with upgrade after quantity is determined
	const proceedWithUpgrade = async (license: typeof selectedLicense, proCount: number) => {
		if (!license) return;

		const isOneTime = license.payment_interval === PaymentInterval.OneTime;
		if (isOneTime) {
			// One-time purchase: redirect to Stripe checkout
			setIsRedirectingToCheckout(true);
			try {
				const result = await trpcContext.premium.getOneTimeUpgradeCheckout.fetch({
					entitlement_id: license.id,
					pro_count: proCount,
				});
				if (result.url) {
					window.location.href = result.url;
				}
			} catch (error) {
				setIsRedirectingToCheckout(false);
				console.error("Failed to get checkout URL:", error);
			}
		} else {
			// Subscription: show confirmation modal
			setSelectedProCount(proCount);
			setShowSubscriptionConfirm(true);
		}
	};

	// Handle upgrade button click from license listing
	const handleUpgradeClick = (license: {
		id: string;
		product_id: string;
		is_bundle: boolean;
		available_pro_slots: number;
		current_pro_addon_count: number;
		payment_interval?: number;
	}) => {
		setSelectedLicense(license);
		if (license.is_bundle && license.available_pro_slots > 1) {
			// Bundle with multiple slots - show quantity picker
			setShowQuantityModal(true);
		} else {
			// Single license or bundle with 1 slot - go directly to confirmation/checkout
			proceedWithUpgrade(license, 1);
		}
	};

	// Handle quantity selection for bundles
	const handleQuantitySelect = (count: number) => {
		setShowQuantityModal(false);
		proceedWithUpgrade(selectedLicense, count);
	};

	// Handle subscription upgrade success
	const handleUpgradeSuccess = () => {
		setShowSubscriptionConfirm(false);
		setSelectedLicense(null);

		// Show Pro activation modal
		setPendingUnassignedCount(selectedProCount);
		setActivationType("pro");
		setShowActivationModal(true);

		// Invalidate queries to refresh data
		trpcContext.premium.invalidate();
	};

	// Handle server activation assignment
	const handleActivationAssignment = (guildIds: string[]) => {
		assignProMutation.mutate({ guild_ids: guildIds });
	};

	return (
		<>
			<DashboardContainer
				title="Premium"
				size="lg"
				bread_crumbs={
					<BackButton
						label="Back"
						onClick={() => {
							history.push(props.back_link ?? "/dashboard/");
						}}
					/>
				}
			>
				<div className="flex flex-col gap-8">
					{/* Sale banner when coupon is active */}
					{prices_query.data.active_coupon && (
						<SaleBanner coupon={prices_query.data.active_coupon} />
					)}

					<PremiumActivePurchaseListings
						premium_details_query={premium_details_query}
						upgradeable_licenses={upgradeableLicensesQuery.data?.licenses}
						onUpgrade={handleUpgradeClick}
					/>

					<PricingTable
						link_type="Link"
						current_pricing={prices_query.data}
						has_used_free_trial={
							premium_details_query.data
								.has_used_stripe_free_trial
						}
					/>
					<div className="ml-auto">
						<RestoreDiscordPurchasesButton />
					</div>

					{/* Visual feature showcase */}
					<GifFeatureSections />

					{/* Feature comparison table */}
					<FeatureComparisonTable />

					{/* FAQ section */}
					<PremiumFaq />

					{/* Custom bot card at bottom */}
					<div className="mt-16">
						<CustomBotCard
							onContactClick={() => setShowContactModal(true)}
						/>
					</div>
				</div>
			</DashboardContainer>

			{/* Contact modal */}
			{showContactModal && (
				<SimpleContactModal
					onClose={() => setShowContactModal(false)}
				/>
			)}

			{/* Pro Quantity Modal for bundle upgrades */}
			{selectedLicense && (
				<ProQuantityModal
					open={showQuantityModal}
					onClose={() => {
						setShowQuantityModal(false);
						setSelectedLicense(null);
					}}
					availableSlots={selectedLicense.available_pro_slots}
					currentProCount={selectedLicense.current_pro_addon_count}
					onConfirm={handleQuantitySelect}
				/>
			)}

			{/* Subscription Upgrade Confirmation Modal */}
			{selectedLicense && (
				<SubscriptionUpgradeModal
					open={showSubscriptionConfirm}
					onClose={() => {
						setShowSubscriptionConfirm(false);
						setSelectedLicense(null);
					}}
					entitlementId={selectedLicense.id}
					proCount={selectedProCount}
					isBundle={selectedLicense.is_bundle}
					onSuccess={handleUpgradeSuccess}
				/>
			)}

			{/* Server Activation Modal */}
			<ServerActivationModal
				open={showActivationModal}
				onClose={() => {
					setShowActivationModal(false);
					setPendingUnassignedCount(null);
				}}
				type={activationType}
				unassignedCount={pendingUnassignedCount ?? 0}
				guilds={guild_list}
				onAssign={handleActivationAssignment}
			/>
		</>
	);
}
